import { finderScopeProps } from "#ce/shared/filters/props/finder-scope";
import { booleanAttribute } from "#ce/utils/props";
import { navigationOptions } from "#ce/components/SpQuantumSelect/props";

export const props = Object.freeze({
  ...finderScopeProps,
  /** Disables the filter's interactive controls. */
  disabled: booleanAttribute(),
  /**
   * The selected category ids. A server-rendered mount passes a JSON array as a string, or one
   * bare id (`"106"`), the form a single-value URL param carries; a Vue host binds an array. The model is an array in both modes; under `single` it holds at most one id,
   * and a value carrying several keeps the first.
   *
   * @type {Array|String}
   */
  modelValue: {
    type: [Array, String],
    default: undefined,
  },
  /**
   * The categories offered, as `[{ id, name, group, icon, visibility, children }]` nodes. A
   * server-rendered mount passes them as a JSON string. `id` is the `slug`, else the `value`, the
   * scope endpoint serves for the node, compared as a string. Every field but `id` and `name` is
   * optional:
   *
   * - `group` names the section header a top-level node renders under. Nodes without one are
   *   loose options: they render as a headerless block above every group, in their authored
   *   order, wherever they sit in the array.
   * - `icon` is an `sp-icon` name rendered before the node's name.
   * - `visibility` is `"search"` for a node offered only while the visitor is searching, or
   *   `"browse"` for one offered only while they are not. Omitted, the node is offered in both
   *   states.
   * - `children` nests further nodes under a node that is itself selectable. `accordion` and
   *   `drilldown` render them; `flat` renders the top level alone.
   *
   * @type {Array|String}
   */
  items: {
    type: [Array, String],
    default: () => [],
  },
  /**
   * How the list is traversed. `flat` renders one always-visible list; `accordion` expands a
   * node's children inline beneath it; `drilldown` shows one level at a time.
   *
   * @type {String}
   * @values flat, accordion, drilldown
   */
  navigation: {
    type: String,
    default: "flat",
    validator: (value) => navigationOptions.includes(value),
  },
  /**
   * Endpoint the ids in scope are read from when the declared scope changes, requested as given
   * with `scope[<filter>]` params appended. It must serve the section `name` denotes as rows
   * carrying `slug` or `value`. Without it the list is not narrowed: scope changes make no request and every
   * item stays offered.
   */
  url: {
    type: String,
    default: undefined,
  },
  /**
   * Form field name each selected id is submitted under — one entry per id, the way a native
   * `<select multiple>` submits. Under `single` that is one entry.
   */
  name: {
    type: String,
    default: "categories[]",
  },
  /**
   * Offer one category at a time: a pick replaces the current one and closes the menu, and rows
   * render without checkboxes. Off, the picker is a checkbox multi-select that stays open.
   *
   * @type {Boolean|String}
   */
  single: booleanAttribute(),
  /**
   * Title of a clearing row offered first in the list ("Any Gender"). The row is marked while
   * nothing is selected, picking it clears the selection, and a scope that admits no option leaves
   * it in place. Omitted, or under `non-clearable`, no such row is offered. Per mount, because the
   * wording differs per filter and per publishing domain.
   */
  anyLabel: {
    type: String,
    default: undefined,
  },
  /**
   * The `data-testid` each option row carries, a value from the test-id catalog. Defaults to the
   * events page's row id.
   */
  optionTestId: {
    type: String,
    default: undefined,
  },
  /** The `data-testid` the list's search input carries. Defaults to the events page's search id. */
  searchTestId: {
    type: String,
    default: undefined,
  },
  /** The `data-testid` the title bar's clear action carries. Defaults to the events page's clear id. */
  clearTestId: {
    type: String,
    default: undefined,
  },
  /** The `data-testid` the clearing row carries in place of `optionTestId`. */
  anyOptionTestId: {
    type: String,
    default: undefined,
  },
  /** The `data-testid` of every branch row's disclosure toggle, from the test-id catalog. */
  branchToggleTestId: {
    type: String,
    default: undefined,
  },
  /** Title of the open list ("Provider's Gender"). Defaults to `label`. */
  menuTitle: {
    type: String,
    default: undefined,
  },
  /** The activator label — the category group's name ("Type"). Per mount, so a prop. */
  label: {
    type: String,
    required: true,
  },
  prependIcon: {
    type: String,
    default: undefined,
  },
  appendIcon: {
    type: String,
    default: "popup-arrow-filled",
  },
  /**
   * Hide the list's search box, for a short list that needs no filtering.
   *
   * @type {Boolean|String}
   */
  noSearch: booleanAttribute(),
  /**
   * Draw each option's icon as a white glyph on a round badge, the look of the finder's result-row
   * icons. Off, the icon is a bare glyph.
   *
   * @type {Boolean|String}
   */
  iconBadge: booleanAttribute(),
  /**
   * Hide the pointer that joins the menu to its activator.
   *
   * @type {Boolean|String}
   */
  hidePointer: booleanAttribute(),
  /** @type {Boolean|String} */
  open: booleanAttribute(),
  location: {
    type: String,
    default: undefined,
  },
  /** @type {Boolean|String} */
  readonly: booleanAttribute(),
  /** Requires a selection before form submission. @type {Boolean|String} */
  required: booleanAttribute(),
  /** @type {Boolean|String} */
  nonClearable: booleanAttribute(),
});
